import styled from "@emotion/styled"
import Image from "next/image"
import colors from "theme/colors"
import { Desktop, Mobile, NewText, TitleWithLine } from "ui"
import { extractProperty } from "utils"

import { httpsImage } from "utils/utils"
import BoxItem from "./BoxItem"

const AboutSectionWrapper = styled.div<{ en?: boolean }>`
  position: relative;
  display: grid;
  row-gap: 40px;
  ${(props) => props.theme.breakpoints.down("sm")} {
    ${({ en }) => (en ? "padding-right:20px" : "padding-left:20px;")}
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    column-gap: 53px;
    grid-template-columns: 1fr 1fr;
    padding-left: 146px;
    padding-right: 146px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    column-gap: 80px;
    padding-left: 220px;
    padding-right: 220px;
  }
`

const StyledImageWrapper = styled.div`
  position: relative;
  height: 480px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 533px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 800px;
  }
`

const StyledTextContent = styled.div<{ locale?: string }>`
  ${(props) => props.theme.breakpoints.down("sm")} {
    ${({ locale }) =>
      locale === "en" ? "padding-left:20px" : "padding-right:20px;"}
  }
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-top: ${({ locale }) => (locale === "en" ? "0" : "106")}px;
    padding-bottom: ${({ locale }) => (locale === "en" ? "0" : "106")}px;
    gap: 18px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 28px;
    padding-top: ${({ locale }) => (locale === "en" ? "0" : "136")}px;
    padding-bottom: ${({ locale }) => (locale === "en" ? "0" : "136")}px;
  }
  p {
    margin: 0px;
  }
  h3,
  h4 {
    margin: 0;
    font-weight: 400;
  }
`

const StyledLinesBox = styled.div`
  position: absolute;
  display: flex;
  bottom: 0;
  flex-wrap: wrap;
  padding-bottom: 0px;
  justify-content: space-evenly;
  z-index: 2;
  padding-left: 10px;
  padding-right: 10px;
  padding-bottom: 15px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-left: 14px;
    padding-right: 14px;
    padding-bottom: 16px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-left: 21px;
    padding-right: 21px;
    padding-bottom: 40px;
  }
`

const StyledDashedLine = styled.div`
  height: calc(100% - 120px);
  border-right: 2px dotted white;
  width: 2px;
  position: absolute;
  top: 60px;
`

const StyledTextWrapper = styled.div`
  width: 100%;
  padding-bottom: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 18px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-bottom: 27px;
  }
`

const StyledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 24, 49, 0.12) 30.73%,
    #000000 89.58%
  );
  background-blend-mode: multiply;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

function AboutSection({ data, locale }: { data: any; locale: string }) {
  const headline = extractProperty(data, "headline")
  const image = extractProperty(data, "image")
  const body = extractProperty(data, "body")
  const subtitle = extractProperty(data, "subTitle")
  const blockMainTitle = extractProperty(data, "blockMainTitle")
  const innerBlocks = extractProperty(data, "innerBlocks")
  return (
    <AboutSectionWrapper en={locale === "en"}>
      <StyledImageWrapper>
        <StyledOverlay />
        <Desktop>
          <Image quality={85} src={httpsImage(image)} fill alt="about" />
        </Desktop>
        <Mobile>
          <Image quality={85} src={httpsImage(image)} fill alt="about" />
        </Mobile>
        <StyledLinesBox>
          <>
            <StyledTextWrapper>
              <NewText
                component={"h2"}
                largeDesktopFontSize={28}
                largeDesktopLineHeight="36.px"
                desktopFontSize={18}
                desktopLineHeight={"23px"}
                mobileFontSize={20}
                mobileLineHeight={"20px"}
                desktopFontWeight={500}
                color={colors.white}
              >
                {blockMainTitle}
              </NewText>
            </StyledTextWrapper>
            <StyledDashedLine />
            {innerBlocks.map((item: any, index: number) => (
              <BoxItem key={index.toString()} item={item} index={index} />
            ))}
          </>
        </StyledLinesBox>
      </StyledImageWrapper>
      <StyledTextContent locale={locale}>
        <Desktop>
          <NewText
            component={"h1"}
            largeDesktopFontSize={64}
            largeDesktopLineHeight="76.8px"
            desktopFontSize={42}
            desktopLineHeight={"50.4px"}
            mobileFontSize={36}
            mobileLineHeight={"50.4px"}
            desktopFontWeight={500}
          >
            {headline}
          </NewText>
        </Desktop>
        <Mobile>
          <TitleWithLine
            component={"h1"}
            largeDesktopFontSize={64}
            largeDesktopLineHeight="76.8px"
            titleFontSize={42}
            desktopLineHeight={"50.4px"}
            mobileTitleFontSize={36}
            mobileLineHeight={"50.4px"}
            desktopFontWeight={500}
            title={headline}
            titleColor={colors.black}
          />
        </Mobile>
        <NewText
          component={"div"}
          largeDesktopFontSize={28}
          largeDesktopLineHeight="39.2px"
          desktopFontSize={18}
          desktopLineHeight={"25.2px"}
          mobileFontSize={20}
          mobileLineHeight={"28px"}
          // desktopFontWeight={500}
          dangerouslySetInnerHTML={{ __html: subtitle }}
        />
        <NewText
          component={"div"}
          largeDesktopFontSize={18}
          largeDesktopLineHeight="32px"
          desktopFontSize={12}
          desktopLineHeight={"18px"}
          mobileFontSize={16}
          mobileLineHeight={"24px"}
          dangerouslySetInnerHTML={{ __html: body }}
        />
      </StyledTextContent>
    </AboutSectionWrapper>
  )
}

export default AboutSection
